import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, History } from 'lucide-react'
import { superadminApi, type LegalRevisionDto } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { DATA_PROCESSING_TERMS, PHOTO_GUIDE, type LegalDoc } from '@/features/legal/legalContent'
import { docToMarkup, parseLegalMarkup } from '@/features/legal/legalMarkup'
import { LegalDocumentBody, LegalDocumentHeader } from '@/features/legal/LegalDocumentPage'

const DEFAULTS: Record<string, LegalDoc> = {
  'data-processing': DATA_PROCESSING_TERMS,
  'photo-guide': PHOTO_GUIDE,
}

const HELP = `## Heading          starts a numbered section
- item             bullet list
1. item            numbered list
> text             highlighted note
| A | B |          table (first row is the header)
blank line         ends a paragraph`

const fmtDateTime = (d: string) =>
  new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

/** SuperAdmin: edit one in-app legal document, preview it, and see every earlier revision. */
export default function LegalDocumentEditorPage() {
  const { key = '' } = useParams<{ key: string }>()
  const queryClient = useQueryClient()
  const fallback = DEFAULTS[key]

  const { data: saved, isLoading } = useQuery({
    queryKey: ['legal-doc-edit', key],
    queryFn: () => superadminApi.getLegalDoc(key),
    enabled: !!fallback,
  })
  const { data: revisions = [] } = useQuery({
    queryKey: ['legal-doc-revisions', key],
    queryFn: () => superadminApi.getLegalDocRevisions(key),
    enabled: !!fallback,
  })

  const [title, setTitle] = useState<string | null>(null)
  const [subtitle, setSubtitle] = useState('')
  const [effectiveOn, setEffectiveOn] = useState('')
  const [body, setBody] = useState('')
  const [reAccept, setReAccept] = useState(false)
  const [note, setNote] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  // Load once: the saved text if there is any, otherwise the built-in default.
  if (!isLoading && fallback && title === null) {
    setTitle(saved?.title ?? fallback.title)
    setSubtitle(saved?.subtitle ?? fallback.subtitle ?? '')
    setEffectiveOn(saved?.effectiveOn ?? new Date().toISOString().slice(0, 10))
    setBody(docToMarkup(saved ? parseLegalMarkup(saved.body) : fallback))
  }

  const save = useMutation({
    mutationFn: () => superadminApi.saveLegalDoc(key, {
      title: title ?? '', subtitle: subtitle.trim() || null, body, effectiveOn: effectiveOn || null,
      requireReAcceptance: reAccept, changeNote: note.trim() || null,
    }),
    onSuccess: ({ version }) => {
      setMessage(reAccept
        ? `Saved as version ${version}. Customers will be asked to accept it again.`
        : `Saved (version ${version}).`)
      setReAccept(false)
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['legal-doc-edit', key] })
      queryClient.invalidateQueries({ queryKey: ['legal-doc-revisions', key] })
      queryClient.invalidateQueries({ queryKey: ['public-legal-doc', key] })
      queryClient.invalidateQueries({ queryKey: ['data-processing-status'] })
    },
    onError: () => setMessage('Could not save. Check the title and text are not empty.'),
  })

  if (!fallback) return <p className="text-sm text-gray-500">Unknown document.</p>
  if (isLoading || title === null) return <p className="text-sm text-gray-400">Loading…</p>

  const preview = parseLegalMarkup(body)
  const isTerms = key === 'data-processing'
  const loadRevision = (r: LegalRevisionDto) => {
    setTitle(r.title)
    setSubtitle(r.subtitle ?? '')
    setEffectiveOn(r.effectiveOn)
    setBody(docToMarkup(parseLegalMarkup(r.body)))
    setMessage(`Loaded the text saved ${fmtDateTime(r.savedAt)}. Save to make it current again.`)
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/superadmin/legal" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft size={13} /> Legal documents
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{fallback.title}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {saved
            ? `Current version ${saved.version}, last saved ${fmtDateTime(saved.updatedAt)}${saved.updatedBy ? ` by ${saved.updatedBy}` : ''}.`
            : 'Showing the built-in text. Save to publish your own version.'}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Title
              <input value={title} onChange={e => setTitle(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-normal" />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Effective date
              <input type="date" value={effectiveOn} onChange={e => setEffectiveOn(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-normal" />
            </label>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            Subtitle
            <input value={subtitle} onChange={e => setSubtitle(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-normal" />
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Text
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={28}
              spellCheck
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono leading-relaxed"
            />
          </label>
          <pre className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap">{HELP}</pre>

          {isTerms && (
            <label className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={reAccept} onChange={e => setReAccept(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-xs text-amber-900">
                <strong>This changes what customers agreed to: ask them to accept again.</strong> Leave unticked for typos and clarifications.
                Every save is recorded either way.
              </span>
            </label>
          )}

          <label className="block text-sm font-medium text-gray-700">
            What changed (kept in the history)
            <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="e.g. Added Resend region" className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-normal" />
          </label>

          <div className="flex items-center gap-3">
            <Button onClick={() => { setMessage(null); save.mutate() }} disabled={save.isPending || !title.trim() || !body.trim()}>
              {save.isPending ? 'Saving…' : reAccept ? 'Save new version' : 'Save'}
            </Button>
            {message && <span className="flex items-center gap-1 text-sm text-gray-700"><Check size={14} className="text-green-600" /> {message}</span>}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview</p>
          <div className="border border-gray-200 rounded-xl p-6 bg-white max-h-[46rem] overflow-y-auto">
            <LegalDocumentHeader title={title} subtitle={subtitle} meta={`Version ${(saved?.version ?? 1) + (reAccept ? 1 : 0)}`} />
            <LegalDocumentBody intro={preview.intro} sections={preview.sections} />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 mb-2"><History size={15} /> History</h2>
        {revisions.length === 0 ? (
          <p className="text-sm text-gray-400">No saved versions yet.</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-w-3xl">
            {revisions.map((r, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="text-gray-900">
                    Version {r.version} <span className="text-gray-400">· {fmtDateTime(r.savedAt)}{r.savedBy ? ` · ${r.savedBy}` : ''}</span>
                  </p>
                  {r.changeNote && <p className="text-xs text-gray-500 mt-0.5">{r.changeNote}</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => loadRevision(r)}>Load into editor</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
